import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit2, Trash2, X, Upload, Package, Search, Tag, Boxes, AlertTriangle, CheckCircle2 } from 'lucide-react';
import apiFetch, { baseURL } from '../../../../utils/api';
import { invalidate } from '../../../../utils/cache';
import './AdminEcommerce.css';

const resolveImage = (img) => {
    if (!img) return null;
    if (img.startsWith('http') || img.startsWith('blob:') || img.startsWith('data:')) return img;
    return `${baseURL}${img}`;
};

const AdminProducts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [saving, setSaving] = useState(false);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        stock: '',
        category: 'General',
        is_active: 'true'
    });
    const [imageFile, setImageFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await apiFetch('/api/admin/products');
            const data = await response.json();
            setProducts(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to fetch products');
        } finally {
            setLoading(false);
        }
    };

    const stats = useMemo(() => {
        const total = products.length;
        const active = products.filter(p => p.is_active).length;
        const lowStock = products.filter(p => p.stock > 0 && p.stock <= 5).length;
        const outOfStock = products.filter(p => p.stock === 0).length;
        return { total, active, lowStock, outOfStock };
    }, [products]);

    const filteredProducts = useMemo(() => {
        return products.filter(p => {
            if (statusFilter === 'active' && !p.is_active) return false;
            if (statusFilter === 'inactive' && p.is_active) return false;
            if (statusFilter === 'low' && !(p.stock > 0 && p.stock <= 5)) return false;
            if (statusFilter === 'out' && p.stock !== 0) return false;
            if (search) {
                const q = search.toLowerCase();
                return (p.name || '').toLowerCase().includes(q) ||
                       (p.category || '').toLowerCase().includes(q);
            }
            return true;
        });
    }, [products, search, statusFilter]);

    const handleOpenModal = (product = null) => {
        if (product) {
            setEditingProduct(product);
            setFormData({
                name: product.name,
                description: product.description || '',
                price: product.price,
                stock: product.stock,
                category: product.category,
                is_active: String(product.is_active)
            });
            setPreviewUrl(product.image || null);
        } else {
            setEditingProduct(null);
            setFormData({
                name: '',
                description: '',
                price: '',
                stock: '10',
                category: 'General',
                is_active: 'true'
            });
            setPreviewUrl(null);
        }
        setImageFile(null);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setImageFile(null);
        setPreviewUrl(null);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        const data = new FormData();
        data.append('name', formData.name);
        data.append('description', formData.description);
        data.append('price', formData.price);
        data.append('stock', formData.stock);
        data.append('category', formData.category);
        data.append('is_active', formData.is_active);
        if (imageFile) {
            data.append('image', imageFile);
        }

        try {
            const url = editingProduct
                ? `/api/admin/products/${editingProduct.id}/update`
                : '/api/admin/products/create';

            const response = await apiFetch(url, {
                method: 'POST',
                body: data,
            });

            const result = await response.json();
            if (result.success) {
                invalidate('/api/products');
                invalidate('/api/admin/products');
                handleCloseModal();
                fetchProducts();
            } else {
                alert(result.message || 'Failed to save product.');
            }
        } catch (err) {
            alert('Failed to save product');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (pk) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;
        try {
            const response = await apiFetch(`/api/admin/products/${pk}/delete`, { method: 'DELETE' });
            const result = await response.json();
            if (result.success) {
                invalidate('/api/products');
                invalidate('/api/admin/products');
                fetchProducts();
            }
        } catch (err) {
            alert('Failed to delete');
        }
    };

    const getStockBadge = (stock) => {
        if (stock === 0) return <span className="stock-tag out">Out of stock</span>;
        if (stock <= 5) return <span className="stock-tag low">Low ({stock})</span>;
        return <span className="stock-tag ok">{stock} in stock</span>;
    };

    return (
        <div className="admin-ecom-page">
            <div className="admin-ecom-header">
                <div>
                    <h1>Inventory Management</h1>
                    <p>Manage your products, stock, and pricing.</p>
                </div>
                <button className="admin-add-btn" onClick={() => handleOpenModal()}>
                    <Plus size={18} /> Add New Product
                </button>
            </div>

            {/* Stats Summary */}
            <div className="admin-ecom-stats">
                <div className="ecom-stat-card">
                    <div className="ecom-stat-icon stat-blue"><Boxes size={20} /></div>
                    <div>
                        <p className="ecom-stat-label">Total Products</p>
                        <h3>{stats.total}</h3>
                    </div>
                </div>
                <div className="ecom-stat-card">
                    <div className="ecom-stat-icon stat-green"><CheckCircle2 size={20} /></div>
                    <div>
                        <p className="ecom-stat-label">Active</p>
                        <h3>{stats.active}</h3>
                    </div>
                </div>
                <div className="ecom-stat-card">
                    <div className="ecom-stat-icon stat-amber"><AlertTriangle size={20} /></div>
                    <div>
                        <p className="ecom-stat-label">Low Stock</p>
                        <h3>{stats.lowStock}</h3>
                    </div>
                </div>
                <div className="ecom-stat-card">
                    <div className="ecom-stat-icon stat-red"><Package size={20} /></div>
                    <div>
                        <p className="ecom-stat-label">Out of Stock</p>
                        <h3>{stats.outOfStock}</h3>
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="admin-ecom-toolbar">
                <div className="admin-search-input">
                    <Search size={16} />
                    <input
                        type="text"
                        placeholder="Search by name or category..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="admin-filter-chips">
                    {[
                        { key: 'all', label: 'All' },
                        { key: 'active', label: 'Active' },
                        { key: 'inactive', label: 'Inactive' },
                        { key: 'low', label: 'Low Stock' },
                        { key: 'out', label: 'Out of Stock' }
                    ].map(f => (
                        <button
                            key={f.key}
                            className={`admin-chip ${statusFilter === f.key ? 'active' : ''}`}
                            onClick={() => setStatusFilter(f.key)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="admin-ecom-loading">
                    <div className="ec-spinner" />
                    <p>Loading inventory...</p>
                </div>
            ) : filteredProducts.length === 0 ? (
                <div className="admin-ecom-empty">
                    <Package size={36} />
                    <h3>No products yet</h3>
                    <p>Add your first product to start selling.</p>
                    <button className="admin-add-btn" onClick={() => handleOpenModal()}>
                        <Plus size={18} /> Add Product
                    </button>
                </div>
            ) : (
                <div className="admin-product-table-wrap">
                    <table className="admin-product-table">
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th>Category</th>
                                <th>Price</th>
                                <th>Stock</th>
                                <th>Status</th>
                                <th className="actions-col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProducts.map(p => (
                                <tr key={p.id}>
                                    <td>
                                        <div className="admin-p-info">
                                            <div className="admin-p-thumb">
                                                {p.image
                                                    ? <img src={resolveImage(p.image)} alt={p.name} />
                                                    : <Package size={20} />}
                                            </div>
                                            <div className="admin-p-name">
                                                <strong>{p.name}</strong>
                                                <span>ID #{p.id}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td><span className="admin-cat-pill"><Tag size={12} /> {p.category}</span></td>
                                    <td className="price-cell">Rs. {parseFloat(p.price).toLocaleString()}</td>
                                    <td>{getStockBadge(p.stock)}</td>
                                    <td>
                                        <span className={`admin-status-tag ${p.is_active ? 'active' : 'inactive'}`}>
                                            <span className="dot" />
                                            {p.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="admin-p-actions">
                                            <button className="icon-btn" title="Edit" onClick={() => handleOpenModal(p)}><Edit2 size={15} /></button>
                                            <button className="icon-btn delete" title="Delete" onClick={() => handleDelete(p.id)}><Trash2 size={15} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="admin-ecom-modal" onClick={handleCloseModal}>
                    <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <div>
                                <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
                                <p className="modal-sub">
                                    {editingProduct ? `Update details for "${editingProduct.name}"` : 'Fill in product details and upload an image'}
                                </p>
                            </div>
                            <button className="admin-modal-close" onClick={handleCloseModal}><X size={20} /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="admin-product-form">
                            <div className="admin-form-body">
                                {/* Image column */}
                                <div className="admin-form-image-col">
                                    <label className="admin-form-label">Product Image</label>
                                    <div className="admin-image-upload">
                                        {previewUrl ? (
                                            <div className="admin-image-preview">
                                                <img src={resolveImage(previewUrl)} alt="Preview" />
                                                <div className="admin-image-actions">
                                                    <label className="admin-change-btn">
                                                        <Upload size={14} /> Change
                                                        <input type="file" accept="image/*" onChange={handleImageChange} hidden />
                                                    </label>
                                                    <button
                                                        type="button"
                                                        className="admin-remove-btn"
                                                        onClick={() => { setImageFile(null); setPreviewUrl(null); }}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <label className="admin-upload-placeholder">
                                                <Upload size={28} />
                                                <span className="primary">Click to upload</span>
                                                <span className="hint">PNG, JPG up to 5MB</span>
                                                <input type="file" accept="image/*" onChange={handleImageChange} hidden />
                                            </label>
                                        )}
                                    </div>
                                </div>

                                {/* Fields column */}
                                <div className="admin-form-fields-col">
                                    <div className="admin-form-group">
                                        <label>Product Name *</label>
                                        <input
                                            type="text" required
                                            value={formData.name}
                                            placeholder="e.g. Wireless Bluetooth Headphones"
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-form-row">
                                        <div className="admin-form-group">
                                            <label>Price (PKR) *</label>
                                            <input
                                                type="number" step="0.01" required
                                                value={formData.price}
                                                placeholder="0.00"
                                                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                            />
                                        </div>
                                        <div className="admin-form-group">
                                            <label>Stock Quantity *</label>
                                            <input
                                                type="number" required min="0"
                                                value={formData.stock}
                                                onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                                                style={{ width: '80%' }}
                                            />
                                        </div>
                                    </div>
                                    <div className="admin-form-row">
                                        <div className="admin-form-group">
                                            <label>Category</label>
                                            <input
                                                type="text"
                                                value={formData.category}
                                                placeholder="General"
                                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                            />
                                        </div>
                                        <div className="admin-form-group">
                                            <label>Status</label>
                                            <select
                                                value={formData.is_active}
                                                onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                                            >
                                                <option value="true">Active — visible on store</option>
                                                <option value="false">Inactive — hidden</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Description</label>
                                        <textarea
                                            rows="4"
                                            value={formData.description}
                                            placeholder="Describe the product, features, materials..."
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="admin-modal-footer">
                                <button type="button" className="admin-cancel-btn" onClick={handleCloseModal}>Cancel</button>
                                <button type="submit" className="admin-save-btn" disabled={saving}>
                                    {saving ? 'Saving...' : (editingProduct ? 'Save Changes' : 'Create Product')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminProducts;
